import { useCallback, useEffect, useMemo } from 'react';

import { useAddCauseSelection, useShowCauseW_Selector } from '@givebacks/api/core';
import { useShowStoreCause } from '@givebacks/api/store';
import { colorTo } from '@givebacks/color-bot';
import { useColorBotQuery } from 'core';
import { getErrorMessage_v2 } from 'core/Utils';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Anchor,
  Box,
  Button,
  Card,
  Group,
  ImageMantine,
  LoadingOverlay,
  Modal,
  Overlay,
  Stack,
  Text,
  ThemeIcon,
  notifications,
} from 'ui';
import { useDisclosure } from 'ui/Hooks';
import { IconArrowLeft, IconVerified } from 'ui/Icons';

import classes from './Cause.module.css';

const CauseModalContent = ({ description }: { description?: string }) => {
  return (
    <Card p="sm" bg="transparent">
      <Stack gap="lg">
        <Text fz="18" fw="600">
          About Us
        </Text>
        <Text fz="14" fw="400">
          {description ?? ''}
        </Text>
      </Stack>
    </Card>
  );
};

const CauseModalFooter = ({
  onContinue,
  isLoading,
}: {
  onContinue: () => void;
  isLoading: boolean;
}) => {
  const navigate = useNavigate();
  return (
    <Modal.AffixedFooter>
      <Group justify="space-between">
        <Button
          onClick={() => navigate(-1)}
          leftSection={<IconArrowLeft size="16px" />}
          variant="transparent"
        >
          Back
        </Button>
        <Button onClick={onContinue} loading={isLoading}>
          Follow & Continue
        </Button>
      </Group>
    </Modal.AffixedFooter>
  );
};

const CausesModalTitle = ({ causeId }: { causeId?: string }) => {
  const { data: cause, isLoading } = useShowCauseW_Selector({
    uuid: causeId,
    enabled: !!causeId,
  });

  const { data: storeCause } = useShowStoreCause({
    causeId: cause?.uuid,
    queryOptions: { options: { include: 'store_active' } },
  });

  const website = useMemo(() => {
    return cause?.links?.find((link) => link.type == 'website')?.url;
  }, [cause?.links]);

  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <Overlay
        style={{
          top: '-100%',
        }}
        zIndex={-1}
        gradient={`radial-gradient(
    100% 100% at 50% -30%,
    var(--cause-page-header--primary) 50%,
    rgba(237, 38, 45, 0) 100%
  )`}
      />
      <Group pb="md">
        <ImageMantine src={cause?.thumbnail?.url} radius={12} maw={100} />

        <Stack c="white">
          <Text fz="16" fw="500">
            {cause?.name}
          </Text>
          <Group gap="4">
            <Anchor fz="12" fw="400" href={website} target="_blank" c="white" underline="always">
              {website}
            </Anchor>
            {/* Verify if this is the correct way to check if the cause is stripe connected */}
            {storeCause?.payments_enabled && (
              <ThemeIcon color="white" variant="transparent" size="sm">
                <IconVerified size="16px" />
              </ThemeIcon>
            )}
          </Group>
        </Stack>
      </Group>
    </>
  );
};

const Cause = () => {
  const [isOpen, { open }] = useDisclosure(false);
  const { causeId } = useParams();
  const { data: cause } = useShowCauseW_Selector({
    uuid: causeId,
    enabled: !!causeId,
  });

  const { mutate: addCauseSelection, isPending: isAddingCauseSelection } = useAddCauseSelection();

  const { data: colorBotData } = useColorBotQuery({
    imageSrc: cause?.logo?.url,
  });

  const navigate = useNavigate();

  useEffect(() => {
    open();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only set to open on mount to ensure there is a transition
  }, []);

  const handleContinue = useCallback(() => {
    if (!cause?.uuid) return;
    void addCauseSelection(
      { causeId: cause?.uuid },
      {
        onSuccess: () => {
          navigate('/onboarding/causes-following');
        },
        onError: (error) => {
          notifications.show({
            message: getErrorMessage_v2(error),
            variant: 'error',
          });
        },
      }
    );
  }, [addCauseSelection, cause?.uuid, navigate]);

  return (
    <Modal
      opened={isOpen}
      onClose={() => {}}
      withCloseButton={false}
      title={<CausesModalTitle causeId={causeId} />}
      style={{
        '--cause-page-header--primary': `${colorTo(colorBotData?.colorShades?.[5] ?? '').hex}`,
        '--cause-page-header--darkened': `${colorTo(colorBotData?.darkColor ?? '').hex}`,
      }}
      classNames={{
        header: classes.CauseModalHeader,
      }}
    >
      <Box style={{ zIndex: 205 }}>
        <CauseModalContent description={cause?.description} />
        <CauseModalFooter onContinue={handleContinue} isLoading={isAddingCauseSelection} />
      </Box>
    </Modal>
  );
};

export { Cause };
